import React from 'react'
import PropTypes from 'prop-types'
import { Map, List, fromJS, is as immutableEqual } from 'immutable'
import classNames from 'classnames'
import { PureDataComponent } from '@aytm/lib/common/components/pure-component'
import { listPipe, stopEvent } from '@aytm/lib/common/func'
import railsPostFetch from '@aytm/lib/common/rails-post-fetch'
import { NEW_PAYMENT_METHOD_VALUE } from '@aytm/lib/rfd-ui/components/payment/select-payment'
import InvoiceTableTextRow from '@aytm/lib/rfd-ui/components/payment/invoice-table/text'
import InvoiceTableInputRow from '@aytm/lib/rfd-ui/components/payment/invoice-table/input'
import InvoiceTableSelectRow from '@aytm/lib/rfd-ui/components/payment/invoice-table/select'
import InvoiceTablePaymentMethodRow from '@aytm/lib/rfd-ui/components/payment/invoice-table/payment-method'
import InvoiceTableDiscountRow from '@aytm/lib/rfd-ui/components/payment/invoice-table/discount'
import InvoiceTablePromoRow from '@aytm/lib/rfd-ui/components/payment/invoice-table/promo'
import InvoiceTableAddressRow from '@aytm/lib/rfd-ui/components/payment/invoice-table/address'

import '@aytm/lib/rfd-ui/styles/payment/invoice-table.scss'

export default class InvoiceTable extends PureDataComponent {
  static propTypes = {
    width: PropTypes.number,
    valuesWidth: PropTypes.number,
    invoice: PropTypes.instanceOf(Map).isRequired,
    additionalData: PropTypes.instanceOf(Map),
    update: PropTypes.func,
    updateURL: PropTypes.string,
    beforeUpdate: PropTypes.func,
    afterUpdate: PropTypes.func,
    onUpdateData: PropTypes.func,
    onError: PropTypes.func
  }

  constructor(props) {
    super(props)
    this.updateByURL = this.updateByURL.bind(this);
    this.update = this.update.bind(this);
    this.refresh = this.refresh.bind(this);
    this.tryOpenPendo = this.tryOpenPendo.bind(this);
    this.gatherUpdateData = this.gatherUpdateData.bind(this);
    this.renderRow = this.renderRow.bind(this);
    this.state = {
      updatingFrom: null,
      lastInvoice: null,
      updateFailedFor: null
    }
    this.updateData = Map()
  }

  updateByURL(updateDataToSend = this.updateData, updatingFrom = this.state.updatingFrom) {
    const { updateURL, afterUpdate, additionalData, onError } = this.props
    const requestData = Map.isMap(additionalData)
      ? additionalData.merge(Map({ data: updateDataToSend }))
      : Map({ data: updateDataToSend })
    railsPostFetch(updateURL, {
      body: JSON.stringify(requestData.toJS())
    })
      .then(response => response.json())
      .then(data => {
        if (this.mounted) this.setState({ updatingFrom: null })
        if ('function' == typeof afterUpdate)
          afterUpdate(
            fromJS(data.invoice ? data.invoice : data.invoice_table ? { table: data.invoice_table } : data || {}),
            this.updateData,
            additionalData
          )
      })
      .catch(_error => {
        if ('function' == typeof onError) onError(updateDataToSend, updatingFrom)
        if (this.mounted)
          this.setState({
            updatingFrom: null,
            updateFailedFor: updatingFrom
          })
      })
  }

  update(withData = Map(), updateFrom = null, requestNewTable = true) {
    const { update, updateURL, afterUpdate, additionalData, beforeUpdate, onUpdateData } = this.props

    if (withData.count()) this.updateData = this.updateData.merge(withData)
    if ('function' == typeof onUpdateData) onUpdateData(this.updateData)

    if (requestNewTable) {
      const updateDataToSend =
        'function' == typeof beforeUpdate
          ? (() => {
              const preProcessedData = beforeUpdate(this.lastInvoice, this.updateData, additionalData)
              return null == preProcessedData ? this.updateData : preProcessedData
            })()
          : this.updateData
      if ('function' == typeof update) {
        if (this.mounted)
          this.setState({
            updatingFrom: updateFrom,
            updateFailedFor: null
          })
        update(this.lastInvoice, updateDataToSend, additionalData, Promise).then(
          invoice => {
            if (this.mounted) this.setState({ updatingFrom: null })
            if ('function' == typeof afterUpdate) afterUpdate(invoice, updateDataToSend, additionalData)
          },
          () => {
            if ('string' == typeof updateURL) this.updateByURL(updateDataToSend, updateFrom)
            else if (this.mounted) this.setState({ updatingFrom: null })
          }
        )
      } else {
        if (this.mounted)
          this.setState({
            updatingFrom: updateFrom,
            updateFailedFor: null
          })
        if ('string' == typeof updateURL) this.updateByURL(updateDataToSend, updateFrom)
        else if (this.mounted) this.setState({ updatingFrom: null })
      }
    }
  }

  refresh(event) {
    const { updatingFrom, updateFailedFor } = this.state
    this.update(Map(), updateFailedFor || updatingFrom, true)
    stopEvent(event)
    return false
  }

  tryOpenPendo(event) {
    if ('object' === typeof window.pendo) {
      window.pendo.showGuideById('IW4Rn9GVh6OGbgYxjn1UaL3kdyU')
      stopEvent(event)
      return false
    } else return true
  }

  UNSAFE_componentWillMount() {
    super.UNSAFE_componentWillMount()
    const { invoice } = this.props
    const table = Map.isMap(invoice) ? invoice.get('table') : null
    if (!List.isList(table) || 0 == table.count()) this.update(Map(), '*')
    this.lastInvoice = invoice
    this.gatherUpdateData(invoice)
  }

  gatherUpdateData(invoice = Map()) {
    const flattenList = (list, to = List()) =>
      to.withMutations(ml => list.reduce((l, r) => (List.isList(r) ? flattenList(r, l) : l.push(r)), ml))
    const table = flattenList(invoice.get('table') || List())

    const filledData = listPipe(
      [
        // get data from table
        updateData =>
          table.reduce((d, r) => {
            if (Map.isMap(r) && r.has('id')) {
              const id = r.get('id')
              switch (r.get('type')) {
                case 'select':
                  if (r.has('select')) {
                    return d.getIn([id, 'value', 'id']) == r.get('select')
                      ? d
                      : d.set(
                          id,
                          Map({
                            type: 'select',
                            value: Map({ id: r.get('select') })
                          })
                        )
                  } else if (!d.has(id)) {
                    return d.set(
                      id,
                      Map({
                        type: 'select',
                        value: Map({ id: r.get('defaultOption') })
                      })
                    )
                  } else return d
                case 'paymentMethod':
                  if (r.has('select')) {
                    return d.getIn([id, 'value', 'id']) == r.get('select')
                      ? d
                      : d.set(
                          id,
                          Map({
                            type: 'paymentMethod',
                            value: Map({ id: r.get('select') })
                          })
                        )
                  } else if (!d.has(id)) {
                    const defaultPaymentMethod = r.get('defaultPaymentMethod')
                    return d.set(
                      id,
                      Map({
                        type: 'paymentMethod',
                        value: Map({
                          id: defaultPaymentMethod,
                          new: defaultPaymentMethod == NEW_PAYMENT_METHOD_VALUE.get('id')
                        })
                      })
                    )
                  } else return d
                case 'input':
                  return d.has(id)
                    ? d
                    : d.set(
                        id,
                        Map({
                          type: 'input',
                          value: r.get('inputValue') || (r.get('money') ? 0 : '')
                        })
                      )
                case 'discount':
                  return d.set(
                    id,
                    Map({
                      type: 'discount',
                      value: r.has('code') ? r.get('code') : null
                    })
                  )
                case 'hidden':
                  return d.set(
                    id,
                    Map({
                      type: 'hidden',
                      value: r.get('value')
                    })
                  )
                case 'text':
                  return null == r.get('checked')
                    ? d
                    : d.set(
                        id,
                        Map({
                          type: 'text',
                          value: Boolean(r.get('checked'))
                        })
                      )
                default:
                  return d
              }
            } else return d
          }, updateData),
        // remove data that is not used in table
        updateData =>
          updateData.reduce(
            (d, _v, k) => (null == table.find(r => k == r.get('id'), null, null) ? d.delete(k) : d),
            updateData
          )
      ],
      this.updateData
    )

    const { onUpdateData } = this.props
    if ('function' == typeof onUpdateData) onUpdateData(filledData)

    if (this.updateData != filledData) this.updateData = filledData
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    super.UNSAFE_componentWillReceiveProps(nextProps)
    if (!immutableEqual(this.props.invoice, nextProps.invoice)) {
      const invoice = !Map.isMap(this.lastInvoice)
        ? nextProps.invoice
        : ((nextInvoice, lastInvoice) => {
            const lastTable = lastInvoice.get('table') || List()
            const table = nextInvoice.get('table') || List()
            const updatedTable = table.reduce((t, r, i) => {
              if ('last' == r.get('type') && r.has('id')) {
                const lastRow = lastTable.find(lr => r.get('id') == lr.get('id'), null, null)
                return lastRow
                  ? t.set(
                      i,
                      listPipe(
                        [
                          lr => ('paymentMethod' == lr.get('type') && lr.has('select') ? lr.delete('select') : lr),
                          lr => ('select' == lr.get('type') && lr.has('select') ? lr.delete('select') : lr)
                        ],
                        lastRow
                      ).merge(r.delete('type'))
                    )
                  : t
              } else return t
            }, table)
            return updatedTable == table ? nextInvoice : nextInvoice.set('table', updatedTable)
          })(nextProps.invoice, this.lastInvoice)
      this.lastInvoice = invoice
      this.gatherUpdateData(invoice)
    }

    if (!immutableEqual(nextProps.additionalData, this.props.additionalData)) {
      setTimeout(() => {
        if (this.mounted) this.update(Map(), 'props.additionalData')
      }, 0)
    }
  }

  //TODO: consider breaking into subrenders
  renderRow(row, index) {
    if (Map.isMap(row)) {
      const { width, valuesWidth } = this.props
      const { updatingFrom, updateFailedFor } = this.state
      const key = row.has('id') ? row.get('id') : `itrow-${index}`
      const disabled = updatingFrom && row.get('id') != updatingFrom

      return [
        <div
          className={classNames(`rfd-ui-invoice-table-row rfd-ui-invoice-table-row-type-${row.get('type')}`, {
            'rfd-ui-invoice-table-row-disabled': disabled,
            'rfd-ui-invoice-table-row-error': Boolean(row.get('error')),
            'rfd-ui-invoice-table-row-warning': Boolean(row.get('warning')),
            'rfd-ui-invoice-table-row-bold': Boolean(row.get('bold')),
            'rfd-ui-invoice-table-row-green': Boolean(row.get('success')),
            'rfd-ui-invoice-table-row-subrow': Boolean(row.get('subrow')),
            'rfd-ui-invoice-table-row-grouprow': Boolean(row.get('grouprow')),
            'rfd-ui-invoice-table-row-lightgrouprow': Boolean(row.get('lightgrouprow')),
            'rfd-ui-invoice-table-row-comment': Boolean(row.get('comment')),
            'rfd-ui-invoice-table-row-groupfirstline': Boolean(row.get('groupfirstrow')),
            'rfd-ui-invoice-table-row-confirstline': Boolean(row.get('connectedfirstrow'))
          })}
          key={key}
        >
          {(() => {
            switch (row.get('type')) {
              case 'text':
                return (
                  <InvoiceTableTextRow
                    row={row}
                    width={width}
                    valuesWidth={valuesWidth}
                    onChange={this.update}
                    updatingFrom={updatingFrom}
                  />
                )
              case 'input':
                return (
                  <InvoiceTableInputRow
                    row={row}
                    width={width}
                    valuesWidth={valuesWidth}
                    onChange={this.update}
                    updatingFrom={updatingFrom}
                  />
                )
              case 'discount':
                return (
                  <InvoiceTableDiscountRow
                    row={row}
                    width={width}
                    valuesWidth={valuesWidth}
                    onChange={this.update}
                    updatingFrom={updatingFrom}
                  />
                )
              case 'address':
                return (
                  <InvoiceTableAddressRow
                    row={row}
                    width={width}
                    valuesWidth={valuesWidth}
                    onChange={this.update}
                    updatingFrom={updatingFrom}
                  />
                )
              case 'select':
                return (
                  <InvoiceTableSelectRow
                    row={row}
                    width={width}
                    valuesWidth={valuesWidth}
                    onChange={this.update}
                    updateData={this.updateData}
                    updatingFrom={updatingFrom}
                  />
                )
              case 'paymentMethod':
                return (
                  <InvoiceTablePaymentMethodRow
                    row={row}
                    width={width}
                    valuesWidth={valuesWidth}
                    onChange={this.update}
                    updateData={this.updateData}
                    updatingFrom={updatingFrom}
                  />
                )
              case 'promo':
                return (
                  <InvoiceTablePromoRow row={row} width={width} valuesWidth={valuesWidth} updatingFrom={updatingFrom} />
                )
              default:
                return null
            }
          })()}
        </div>,
        null != updateFailedFor && updateFailedFor == row.get('id') ? (
          <div className="rfd-ui-invoice-table-row rfd-ui-invoice-table-row-error rfd-ui-invoice-table-row-type-update-error rfd-ui-invoice-table-row-type-text rfd-ui-invoice-table-row-subrow">
            <div className="rfd-ui-invoice-table-row-bordered-container">
              <div className="rfd-ui-invoice-table-row-label">
                <div className="rfd-ui-invoice-table-text">
                  Oops! Something went wrong!
                  <a onClick={this.refresh} href="#">
                    &nbsp;Retry
                  </a>{' '}
                  or
                  <a href="/resources/contact-us" target="_blank" onClick={this.tryOpenPendo}>
                    &nbsp;let us know
                  </a>
                  !
                </div>
              </div>
            </div>
          </div>
        ) : null
      ]
    } else if (List.isList(row)) {
      return (
        <div className="rfd-ui-invoice-table-rows-block" key={`itblock-${index}`}>
          {row.map((r, i) => this.renderRow(r, `${index}-${i}`)).toArray()}
        </div>
      )
    }
    return null
  }

  render() {
    const { invoice, width, valuesWidth } = this.props
    const { updatingFrom, updateFailedFor } = this.state

    const table = Map.isMap(this.lastInvoice)
      ? this.lastInvoice.get('table')
      : Map.isMap(invoice)
      ? invoice.get('table')
      : null
    const tableWidth = width && valuesWidth ? width + valuesWidth : null

    return (
      <div className="rfd-ui-invoice-table" style={{ width: tableWidth + 6 }} ref={this.saveRef('Invoice')}>
        {'*' == updatingFrom && (
          <div className="rfd-ui-invoice-table-loading rfd-ui-invoice-table-loading-row">
            <i className="icon-aytmfd-spin afd-spin" />
          </div>
        )}
        {'*' == updateFailedFor && (
          <div className="rfd-ui-invoice-table-row rfd-ui-invoice-table-row-error rfd-ui-invoice-table-row-type-update-error rfd-ui-invoice-table-row-type-text rfd-ui-invoice-table-row-subrow">
            <div className="rfd-ui-invoice-table-row-bordered-container">
              <div className="rfd-ui-invoice-table-row-label">
                <div className="rfd-ui-invoice-table-text">
                  Oops! Something went wrong!
                  <a onClick={this.refresh} href="#">
                    &nbsp;Retry
                  </a>{' '}
                  or
                  <a href="/resources/contact-us" target="_blank" onClick={this.tryOpenPendo}>
                    &nbsp;let us know
                  </a>
                  !
                </div>
              </div>
            </div>
          </div>
        )}
        {List.isList(table) &&
          (() => table.map((r, i) => (Map.isMap(r) || List.isList(r) ? this.renderRow(r, i) : null)).toArray())()}
      </div>
    )
  }
}
